import type { PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { useTranslation } from "react-i18next";
import checkCircleIcon from "../../assets/check-circle.svg";
import styles from "./PricingCard.module.css";

interface Feature {
	text: string;
	included: boolean;
}

interface PricingCardProps {
	packageData: PurchasesPackage;
	planName: string;
	features: Feature[];
	isPopular?: boolean;
	onChoosePlan: (pkg: PurchasesPackage) => void;
	isLoading?: boolean;
	isCurrentPlan?: boolean;
	userHasSubscription?: boolean;
}

export function PricingCard({
	packageData,
	planName,
	features,
	isPopular = false,
	onChoosePlan,
	isLoading = false,
	isCurrentPlan = false,
	userHasSubscription = false,
}: PricingCardProps) {
	const { t } = useTranslation("pricing");
	// Format price from package
	const isFree = packageData.identifier === "core_free";

	// Extract price - handle both native and web SDK formats
	const webPackage = packageData as unknown as {
		rcBillingProduct?: { currentPrice?: { formattedPrice: string } };
		webBillingProduct?: { currentPrice?: { formattedPrice: string } };
	};
	const nativePackage = packageData as unknown as {
		product?: { priceString: string };
	};

	const webProduct =
		webPackage.rcBillingProduct || webPackage.webBillingProduct;
	const rawPrice =
		webProduct?.currentPrice?.formattedPrice ||
		nativePackage.product?.priceString ||
		"$0";

	// Remove decimal part if it's .00
	const price = rawPrice.replace(/\.00$/, "");

	const period = packageData.packageType.includes("ANNUAL")
		? t("billing.year")
		: t("billing.month");

	return (
		<div className={styles.card}>
			{isPopular && (
				<div className={styles.popularBadge}>
					<span>{t("badges.mostPopular")}</span>
				</div>
			)}

			<div className={styles.priceSection}>
				<div className={styles.price}>{price}</div>
				{!isFree && <div className={styles.period}>/{period}</div>}
			</div>

			<div className={styles.planName}>{planName}</div>

			<div className={styles.features}>
				{features.map((feature, index) => (
					<div
						key={index}
						className={`${styles.feature} ${!feature.included ? styles.featureDisabled : ""}`}
					>
						<img
							src={checkCircleIcon}
							alt=""
							className={styles.checkmark}
							width="20"
							height="20"
						/>
						<span>{t(feature.text)}</span>
					</div>
				))}
			</div>

			<button
				type="button"
				className={styles.ctaButton}
				onClick={() => onChoosePlan(packageData)}
				disabled={isLoading || isCurrentPlan}
			>
				{isCurrentPlan
					? t("buttons.currentPlan")
					: isLoading
						? t("buttons.processing")
						: isFree && userHasSubscription
							? t("buttons.cancelSubscription")
							: t("buttons.choosePlan")}
			</button>
		</div>
	);
}
